export class ApiError extends Error {
  status: number;
  code?: string;
  details?: unknown;
  requestId?: string;

  constructor(
    message: string,
    status: number,
    code?: string,
    details?: unknown,
    requestId?: string,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }
}

const API_BASE = (import.meta.env.VITE_API_BASE_URL || "/api/v1").replace(
  /\/$/,
  "",
);
let refreshRequest: Promise<boolean> | null = null;

async function decodeResponse(response: Response) {
  if (response.status === 204) return undefined;
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) return response.json();
  return response.text();
}

export interface ApiEnvelope<T, M = Record<string, unknown>> {
  data: T;
  meta?: M;
}

function unwrapEnvelope<T, M = Record<string, unknown>>(
  payload: unknown,
): ApiEnvelope<T, M> {
  if (payload && typeof payload === "object" && "data" in payload) {
    const envelope = payload as { data: T; meta?: M };
    return {
      data: envelope.data,
      ...(envelope.meta !== undefined ? { meta: envelope.meta } : {}),
    };
  }
  throw new ApiError(
    "服务器响应不符合 API 合同",
    502,
    "invalid_response_envelope",
  );
}

async function refresh(): Promise<boolean> {
  if (!refreshRequest) {
    refreshRequest = fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
    })
      .then(async (response) => {
        if (!response.ok) return false;
        await decodeResponse(response);
        return true;
      })
      .catch(() => false)
      .finally(() => {
        refreshRequest = null;
      });
  }
  return refreshRequest;
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  skipRefresh?: boolean;
}

export async function apiEnvelope<T, M = Record<string, unknown>>(
  path: string,
  options: RequestOptions = {},
): Promise<ApiEnvelope<T, M>> {
  const url = new URL(
    `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`,
    window.location.origin,
  );
  Object.entries(options.query || {}).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "")
      url.searchParams.set(key, String(value));
  });

  const isForm = options.body instanceof FormData;
  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");
  if (!isForm && options.body !== undefined)
    headers.set("Content-Type", "application/json");

  const response = await fetch(url.toString(), {
    ...options,
    credentials: "include",
    headers,
    body:
      options.body === undefined
        ? undefined
        : isForm
          ? (options.body as FormData)
          : JSON.stringify(options.body),
  });

  if (
    response.status === 401 &&
    !options.skipRefresh &&
    !path.includes("/auth/refresh")
  ) {
    const didRefresh = await refresh();
    if (didRefresh)
      return apiEnvelope<T, M>(path, { ...options, skipRefresh: true });
  }

  const payload = await decodeResponse(response);
  if (!response.ok) {
    const container =
      payload && typeof payload === "object"
        ? (payload as Record<string, unknown>)
        : {};
    const error =
      container.error && typeof container.error === "object"
        ? (container.error as Record<string, unknown>)
        : container;
    throw new ApiError(
      String(error.message || error.error || `请求失败（${response.status}）`),
      response.status,
      typeof error.code === "string" ? error.code : undefined,
      error.details,
      typeof error.requestId === "string" ? error.requestId : undefined,
    );
  }
  if (response.status === 204) return { data: undefined as T };
  return unwrapEnvelope<T, M>(payload);
}

export async function api<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  return (await apiEnvelope<T>(path, options)).data;
}

export async function uploadFile(
  path: string,
  file: File,
  field = "file",
  metadata?: Record<string, string>,
  onProgress?: (percent: number) => void,
) {
  return new Promise<unknown>((resolve, reject) => {
    const form = new FormData();
    form.append(field, file);
    Object.entries(metadata || {}).forEach(([key, value]) =>
      form.append(key, value),
    );
    const request = new XMLHttpRequest();
    request.open("POST", `${API_BASE}${path}`);
    request.withCredentials = true;
    request.setRequestHeader("Accept", "application/json");
    request.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable)
        onProgress?.(Math.round((event.loaded / event.total) * 100));
    });
    request.addEventListener("load", () => {
      let payload: unknown;
      try {
        payload = request.responseText
          ? JSON.parse(request.responseText)
          : undefined;
      } catch {
        payload = request.responseText;
      }
      if (request.status >= 200 && request.status < 300)
        resolve(unwrapEnvelope(payload).data);
      else {
        const container =
          payload && typeof payload === "object"
            ? (payload as Record<string, unknown>)
            : {};
        const body =
          container.error && typeof container.error === "object"
            ? (container.error as Record<string, unknown>)
            : container;
        reject(
          new ApiError(
            String(body.message || `上传失败（${request.status}）`),
            request.status,
          ),
        );
      }
    });
    request.addEventListener("error", () =>
      reject(new ApiError("网络连接中断，请重试", 0)),
    );
    request.send(form);
  });
}

export function apiBaseUrl() {
  return API_BASE;
}
